import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useTTS } from "../../../../../hooks/useTTS";
import { setSection, updateSection } from "../../../assignmentSlice";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import CommonPage from "../../common/CommonPage";
import VocaCardBack from "./VocaCardBack";
import VocaCardFront from "./VocaCardFront";

const VocaCardPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const { mode } = useSelector((state) => state.ui);
  const isMobile = mode === "mobile";

  const { speak } = useTTS();

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const currentPage = pageList[currentIndex] || {
    word: "",
    pron: "",
    syn: "",
    ant: "",
    kor: "",
    eng: "",
    example: "",
    example_translation: "",
    isOpened: false,
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    setPageList(
      section.question.words.map((word) => ({
        ...word,
        isOpened: false,
        isUnknown: false,
        example_list: word.example.split("\n"),
        example_translation_list: word.example_translation.split("\n"),
      }))
    );
    setCurrentIndex(0);
  }, [section]);

  useEffect(() => {
    speak({ text: currentPage.word });
    setPageList((prev) =>
      prev.map((word, index) => {
        return index === currentIndex ? { ...word, isOpened: false } : word;
      })
    );
  }, [currentIndex]);

  // 지나온 카드에 '봤다' 표시를 남긴다. 이 표시가 없으면 이어하기 지점을 구하는
  // resumeIndex 가 언제나 0 이 되어, **끝까지 본 아이도 다음에 1번 카드로 돌아왔다**
  // (2026-08-31 무무거모 신고). 저장은 나갈 때 상단바가 한 번에 하므로 여기서는
  // 화면과 redux 만 맞춘다.
  const markStudied = (index) => {
    const newPageList = pageList.map((page, pageIndex) =>
      pageIndex === index ? { ...page, isSubmitted: true } : page
    );
    setPageList(newPageList);
    if (!isPreview && !isReview) {
      dispatch(setSection({ ...section, answer: newPageList }));
    }
    return newPageList;
  };

  const handleNextPage = () => {
    const newPageList = markStudied(currentIndex);

    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      if (!isPreview && !isReview) {
        dispatch(
          updateSection({
            sectionId: section.id,
            data: { answer: newPageList, is_finished: true },
          })
        );
      }
      alert("단어 학습을 완료했습니다.");
      setCurrentStep(2);
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const clickCard = () => {
    speak({ text: currentPage.word });
    setPageList((prev) =>
      prev.map((word, index) => {
        return index === currentIndex
          ? { ...word, isOpened: !word.isOpened }
          : word;
      })
    );
  };

  const clickSwitch = (checked) => {
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isUnknown: checked,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(
      updateSection({ sectionId: section.id, data: { answer: newPageList } })
    );
  };

  return (
    <CommonPage
      currentPage={currentPage}
      currentIndex={currentIndex}
      totalNo={pageList.length}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      fontFamily='"Noto Sans KR", "Pretendard"'
      isMobile={isMobile}
    >
      <div
        style={{
          // 앞뒤 면을 같은 칸에 포갠다. 예전에는 두 면이 모두 absolute 라
          // 이 칸의 높이가 0 이 되어 카드가 아래로 넘쳤고, 그 넘침 때문에
          // 내용이 짧은데도 세로 스크롤바가 생겼다.
          display: "grid",
          alignItems: "start",
          width: "90%",
          maxWidth: "1300px",
          margin: "0 auto", // [정렬] 90% 폭 블록이 왼쪽에 붙던 것 — 중앙 고정
          position: "relative",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s ease",
          transform: currentPage.isOpened ? "rotateY(180deg)" : "rotateY(0deg)",
          cursor: "pointer",
        }}
        onClick={clickCard}
      >
        <VocaCardBack currentPage={currentPage} />
        <VocaCardFront
          currentPage={currentPage}
          currentIndex={currentIndex}
          clickSwitch={clickSwitch}
          onSpeak={() => speak({ text: currentPage.word })}
        />
      </div>
    </CommonPage>
  );
};

export default VocaCardPage;
